import React, { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  getCountFromServer,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  writeBatch,
  where,
  limit,
  startAt,
  endAt,
  documentId,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useSearchParams } from 'react-router-dom';
import { db, functions } from '../../../firebase';
import './customers.css';

const CUSTOMER_SEARCH_LIMIT = 40;
const TIER_BROWSE_LIMIT = 300;
const SMS_BROWSE_LIMIT = 1000;
const SEARCH_DEBOUNCE_MS = 250;
const MIN_NAME_SEARCH_LEN = 2;
const MIN_PHONE_PREFIX_LEN = 3;

/** Mirrors functions/rewards/tierBenefits.js display titles. */
const ACTIVE_TIER_BENEFITS = {
  gold: [
    {
      id: 'gold-services-50',
      title: 'J&Z Gold — 50% off Services',
      description: '50% off alterations & services — auto at checkout',
    },
  ],
  diamond: [
    {
      id: 'diamond-free-shipping',
      title: 'J&Z Diamond — Free Shipping',
      description: 'Free shipping — auto at checkout',
    },
    {
      id: 'diamond-free-alterations',
      title: 'J&Z Diamond — Free Alterations (Services)',
      description: 'Free alterations & services — auto at checkout',
    },
  ],
};

function getCustomerYearlySpend(customer) {
  const year = new Date().getFullYear();
  const spendYear = Number(customer?.rewardsSpendYear);
  const raw = Number(customer?.rewardsYearlySpend);
  if (spendYear !== year || !Number.isFinite(raw) || raw <= 0) return 0;
  return raw;
}

function customerRewardsTierId(customer) {
  const spend = getCustomerYearlySpend(customer);
  if (spend >= 10000) return 'diamond';
  if (spend >= 5000) return 'gold';
  return 'silver';
}

function getActiveTierBenefitsForCustomer(customer) {
  const id = customerRewardsTierId(customer);
  return ACTIVE_TIER_BENEFITS[id] || [];
}

function getExpectedShopifyTierTag(customer) {
  const id = customerRewardsTierId(customer);
  if (id === 'diamond') return 'jz-tier-diamond';
  if (id === 'gold') return 'jz-tier-gold';
  return null;
}

/** True if the customer profile has any linked orders (embedded list or totalOrders). */
function customerHasOrdersInProfile(customer) {
  const orders = customer?.orders;
  if (Array.isArray(orders) && orders.length > 0) return true;
  const total = customer?.totalOrders;
  if (typeof total === 'number' && total > 0) return true;
  if (total != null && String(total).trim() !== '' && Number(total) > 0) return true;
  return false;
}

function getCustomerLoyaltyTier(customer) {
  const id = customerRewardsTierId(customer);
  const name = id === 'gold' ? 'Gold' : id === 'diamond' ? 'Diamond' : 'Silver';
  return { id, name };
}

function customerHasSavedBirthday(customer) {
  const month = Number(customer?.birthdayMonth);
  const day = Number(customer?.birthdayDay);
  if (
    Number.isInteger(month) &&
    month >= 1 &&
    month <= 12 &&
    Number.isInteger(day) &&
    day >= 1 &&
    day <= 31
  ) {
    return true;
  }
  return Boolean(customer?.birthdaySavedAt);
}

function getCustomerLoyaltyPoints(customer) {
  const points = customer?.rewardsPoints;
  if (typeof points === 'number' && !Number.isNaN(points)) return points;
  if (points != null && String(points).trim() !== '') {
    const n = Number(points);
    if (!Number.isNaN(n)) return n;
  }
  return 0;
}

/** Normalize phone: remove ', ", +1 and non-digits; use 10 digits (strip leading 1 if 11 digits) */
function normalizePhone(raw) {
  if (raw == null) return '';
  const cleaned = String(raw).replace(/['"]/g, '').replace(/^r:\s*/i, '').replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) return cleaned.slice(1);
  return cleaned;
}

function capitalizeWords(s) {
  return String(s || '').replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

/**
 * Fast customer search — never scans the full Customers collection.
 * - Exact 10-digit phone → single doc get
 * - Phone prefix (3–9 digits) → documentId range query
 * - Name (2+ letters) → name prefix queries (case variants), capped
 */
async function searchCustomersInFirestore(rawTerm) {
  const term = String(rawTerm || '').trim();
  if (!term) return [];

  const phoneDigits = normalizePhone(term);
  const byId = new Map();

  const addDocs = (snap) => {
    snap.forEach((d) => {
      byId.set(d.id, { id: d.id, ...d.data() });
    });
  };

  // Exact phone lookup (barcode / full number) — O(1)
  if (phoneDigits.length === 10) {
    const snap = await getDoc(doc(db, 'Customers', phoneDigits));
    if (snap.exists()) {
      byId.set(snap.id, { id: snap.id, ...snap.data() });
    }
    return Array.from(byId.values());
  }

  const tasks = [];

  // Partial phone via document id prefix (Customers/{10-digit})
  if (phoneDigits.length >= MIN_PHONE_PREFIX_LEN && phoneDigits.length < 10) {
    tasks.push(
      getDocs(
        query(
          collection(db, 'Customers'),
          orderBy(documentId()),
          startAt(phoneDigits),
          endAt(`${phoneDigits}\uf8ff`),
          limit(CUSTOMER_SEARCH_LIMIT)
        )
      )
    );
  }

  // Name prefix — only when the query has letters
  const hasLetters = /[a-zA-Z]/.test(term);
  if (hasLetters && term.length >= MIN_NAME_SEARCH_LEN) {
    const variants = Array.from(
      new Set([
        term,
        term.toLowerCase(),
        term.toUpperCase(),
        capitalizeWords(term.toLowerCase()),
      ])
    );
    for (const v of variants) {
      tasks.push(
        getDocs(
          query(
            collection(db, 'Customers'),
            orderBy('name'),
            startAt(v),
            endAt(`${v}\uf8ff`),
            limit(CUSTOMER_SEARCH_LIMIT)
          )
        )
      );
    }
  }

  if (tasks.length === 0) return [];

  const snaps = await Promise.all(tasks);
  snaps.forEach(addDocs);
  return Array.from(byId.values()).slice(0, CUSTOMER_SEARCH_LIMIT);
}

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [totalCustomers, setTotalCustomers] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [smsFilter, setSmsFilter] = useState('all'); // 'all', 'opted-in', 'opted-out', 'sms-opt-out-registry'
  const [tierFilter, setTierFilter] = useState('all'); // 'all', 'silver', 'gold', 'diamond'
  const [ordersFilter, setOrdersFilter] = useState('all'); // 'all', 'with-orders', 'without-orders'
  const [birthdayFilter, setBirthdayFilter] = useState('all'); // 'all', 'saved', 'missing'
  /** Normalized doc ids from SMSOptOuts where optedOut === true (loaded only when that filter is used). */
  const [smsOptOutPhones, setSmsOptOutPhones] = useState([]);
  const [smsOptOutsLoaded, setSmsOptOutsLoaded] = useState(false);
  const [browseCounts, setBrowseCounts] = useState(null);
  const [browseCapped, setBrowseCapped] = useState(false);
  const [searchParams] = useSearchParams();
  const [expandedOrders, setExpandedOrders] = useState({});
  const [orderHistoryExpanded, setOrderHistoryExpanded] = useState(false);
  const [alterationsExpanded, setAlterationsExpanded] = useState(false);
  const [customerAlterations, setCustomerAlterations] = useState({});
  const [savingAlterations, setSavingAlterations] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [customersPerPage] = useState(12);
  const [ordersStatusMap, setOrdersStatusMap] = useState({});
  const [pendingCustomerChanges, setPendingCustomerChanges] = useState({});
  const [savingCustomerChanges, setSavingCustomerChanges] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const csvInputRef = useRef(null);
  const autoOpenedPhoneRef = useRef('');
  const searchRequestIdRef = useRef(0);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferFromPhone, setTransferFromPhone] = useState('');
  const [transferToPhone, setTransferToPhone] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferFromPreview, setTransferFromPreview] = useState(null);
  const [transferToPreview, setTransferToPreview] = useState(null);
  const [transferError, setTransferError] = useState('');
  const [transferSuccess, setTransferSuccess] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [tierRepairLoading, setTierRepairLoading] = useState(false);
  const [tierRepairMessage, setTierRepairMessage] = useState('');
  const [tierRepairError, setTierRepairError] = useState('');
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditEventFilter, setAuditEventFilter] = useState('');

  // Parse CSV line handling quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        inQuotes = !inQuotes;
      } else if ((c === ',' && !inQuotes) || (c === '\r' && !inQuotes)) {
        result.push(current.trim());
        current = '';
        if (c === '\r') break;
      } else {
        current += c;
      }
    }
    result.push(current.trim());
    return result;
  };

  const findCustomerByPhone = (phoneRaw, list = customers) => {
    const phone10 = normalizePhone(phoneRaw);
    if (!phone10 || phone10.length !== 10) return null;
    return (
      list.find((c) => normalizePhone(c.id) === phone10 || normalizePhone(c.phone) === phone10) || null
    );
  };

  const resolveCustomerDocId = async (phoneRaw) => {
    const phone10 = normalizePhone(phoneRaw);
    if (!phone10 || phone10.length !== 10) return null;
    const local = findCustomerByPhone(phone10);
    if (local?.id) return { id: local.id, customer: local };
    const snap = await getDoc(doc(db, 'Customers', phone10));
    if (snap.exists()) return { id: snap.id, customer: { id: snap.id, ...snap.data() } };
    return null;
  };

  const resetTransferForm = () => {
    setTransferFromPhone('');
    setTransferToPhone('');
    setTransferAmount('');
    setTransferFromPreview(null);
    setTransferToPreview(null);
    setTransferError('');
    setTransferSuccess('');
    setTransferLoading(false);
  };

  const openTransferModal = () => {
    resetTransferForm();
    setShowTransferModal(true);
  };

  const closeTransferModal = () => {
    if (transferLoading) return;
    setShowTransferModal(false);
    resetTransferForm();
  };

  const refreshTransferPreview = async (which, phoneRaw) => {
    const phone10 = normalizePhone(phoneRaw);
    if (phone10.length !== 10) {
      if (which === 'from') setTransferFromPreview(null);
      else setTransferToPreview(null);
      return;
    }
    try {
      const resolved = await resolveCustomerDocId(phone10);
      const preview = resolved
        ? {
            id: resolved.id,
            name: resolved.customer.name || 'Unknown',
            points: getCustomerLoyaltyPoints(resolved.customer),
            tier: getCustomerLoyaltyTier(resolved.customer).name,
          }
        : { missing: true, phone: phone10 };
      if (which === 'from') setTransferFromPreview(preview);
      else setTransferToPreview(preview);
    } catch (err) {
      console.error('Transfer preview failed:', err);
      if (which === 'from') setTransferFromPreview(null);
      else setTransferToPreview(null);
    }
  };

  const handleTransferPoints = async (e) => {
    e.preventDefault();
    setTransferError('');
    setTransferSuccess('');

    const fromPhone = normalizePhone(transferFromPhone);
    const toPhone = normalizePhone(transferToPhone);
    const amount = Math.floor(Number(transferAmount));

    if (fromPhone.length !== 10 || toPhone.length !== 10) {
      setTransferError('Enter valid 10-digit phone numbers for both customers.');
      return;
    }
    if (fromPhone === toPhone) {
      setTransferError('From and To phone numbers must be different.');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setTransferError('Enter a positive whole number of points to transfer.');
      return;
    }

    setTransferLoading(true);
    try {
      const transferFn = httpsCallable(functions, 'transferRewardsPoints');
      const { data } = await transferFn({ fromPhone, toPhone, amount });

      setCustomers((prev) =>
        prev.map((c) => {
          if (c.id === data.fromId) return { ...c, rewardsPoints: data.fromNext };
          if (c.id === data.toId) return { ...c, rewardsPoints: data.toNext };
          return c;
        })
      );
      setSelectedCustomer((prev) => {
        if (!prev) return prev;
        if (prev.id === data.fromId) return { ...prev, rewardsPoints: data.fromNext };
        if (prev.id === data.toId) return { ...prev, rewardsPoints: data.toNext };
        return prev;
      });

      setTransferFromPreview((prev) =>
        prev && !prev.missing ? { ...prev, points: data.fromNext } : prev
      );
      setTransferToPreview((prev) =>
        prev && !prev.missing ? { ...prev, points: data.toNext } : prev
      );
      setTransferAmount('');
      setTransferSuccess(
        `Transferred ${amount.toLocaleString()} pts from ${data.fromName} to ${data.toName}.`
      );
    } catch (err) {
      console.error('Points transfer failed:', err);
      setTransferError(err?.message || err?.details || 'Transfer failed. Please try again.');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleRepairTierTag = async () => {
    if (!selectedCustomer) return;
    setTierRepairLoading(true);
    setTierRepairMessage('');
    setTierRepairError('');
    try {
      const phone = normalizePhone(selectedCustomer.phone || selectedCustomer.id);
      const repairFn = httpsCallable(functions, 'repairCustomerTierTag');
      const { data } = await repairFn({ phone });
      setSelectedCustomer((prev) =>
        prev
          ? {
              ...prev,
              shopifyId: data.shopifyId || prev.shopifyId,
              rewardsTierShopifySynced: data.rewardsTierShopifySynced,
            }
          : prev
      );
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === selectedCustomer.id
            ? {
                ...c,
                shopifyId: data.shopifyId || c.shopifyId,
                rewardsTierShopifySynced: data.rewardsTierShopifySynced,
              }
            : c
        )
      );
      const tagNote = data.expectedTag
        ? ` Shopify tag: ${data.expectedTag}.`
        : ' Silver — no Gold/Diamond tag needed.';
      setTierRepairMessage(`Synced ${data.tier} tier.${tagNote}`);
    } catch (err) {
      console.error('Tier tag repair failed:', err);
      setTierRepairError(err?.message || 'Could not sync Shopify tier tag.');
    } finally {
      setTierRepairLoading(false);
    }
  };

  const openAuditModal = async () => {
    setShowAuditModal(true);
    setAuditError('');
    setAuditLoading(true);
    try {
      const listFn = httpsCallable(functions, 'listRewardsAuditLog');
      const { data } = await listFn({
        limit: 75,
        event: auditEventFilter || undefined,
      });
      setAuditEntries(data.entries || []);
    } catch (err) {
      console.error('Audit log load failed:', err);
      setAuditError(err?.message || 'Could not load audit log.');
      setAuditEntries([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const refreshAuditLog = async () => {
    setAuditLoading(true);
    setAuditError('');
    try {
      const listFn = httpsCallable(functions, 'listRewardsAuditLog');
      const { data } = await listFn({
        limit: 75,
        event: auditEventFilter || undefined,
      });
      setAuditEntries(data.entries || []);
    } catch (err) {
      setAuditError(err?.message || 'Could not load audit log.');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file, 'UTF-8');
      });
      const lines = text.split(/\n/).filter(l => l.trim());
      if (lines.length < 2) {
        setImportResult({ imported: 0, updated: 0, skipped: 0, errors: ['CSV has no data rows'] });
        return;
      }
      const headerLine = lines[0];
      const headers = parseCSVLine(headerLine).map(h => (h || '').trim());
      const col = (name) => {
        const i = headers.findIndex(h => (h || '').trim() === name);
        return i >= 0 ? i : -1;
      };
      const idxFirst = col('First Name');
      const idxLast = col('Last Name');
      const idxEmail = col('Email');
      const idxAccepts = col('Accepts Email Marketing');
      const idxPhone = col('Phone');
      const idxDefaultPhone = col('Default Address Phone');
      if (idxFirst < 0 || idxLast < 0) {
        setImportResult({ imported: 0, updated: 0, skipped: 0, errors: ['CSV must have "First Name" and "Last Name" columns'] });
        return;
      }

      let imported = 0;
      let updated = 0;
      const errors = [];

      for (let i = 1; i < lines.length; i++) {
        const fields = parseCSVLine(lines[i]);
        const get = (idx) => (idx >= 0 && idx < fields.length ? (fields[idx] || '').trim() : '');
        let phoneRaw = get(idxPhone) || get(idxDefaultPhone);
        const phone = normalizePhone(phoneRaw);
        if (!phone) {
          errors.push(`Row ${i + 1}: No phone (Phone or Default Address Phone)`);
          continue;
        }
        const firstName = get(idxFirst);
        const lastName = get(idxLast);
        const name = [firstName, lastName].filter(Boolean).join(' ').trim() || 'Unknown';
        const email = get(idxEmail);
        const acceptsMarketing = (get(idxAccepts) || '').toLowerCase().trim();
        const smsSubscribed = acceptsMarketing === 'yes';

        const customerRef = doc(db, 'Customers', phone);
        const existing = await getDoc(customerRef);

        const payload = {
          name,
          email: email || '',
          phone,
          smsSubscribed,
        };

        if (existing.exists()) {
          await setDoc(customerRef, { ...payload }, { merge: true });
          updated++;
        } else {
          const data = existing.data() || {};
          await setDoc(customerRef, {
            ...payload,
            orders: data.orders ?? [],
            lastOrderDate: data.lastOrderDate ?? null,
            totalOrders: data.totalOrders ?? 0,
            createdAt: data.createdAt ?? new Date(),
          }, { merge: true });
          imported++;
        }
      }

      setImportResult({ imported, updated, skipped: 0, errors: errors.length ? errors : null });
      if (csvInputRef.current) csvInputRef.current.value = '';
    } catch (err) {
      console.error('CSV import error:', err);
      setImportResult({ imported: 0, updated: 0, skipped: 0, errors: [err.message || 'Import failed'] });
    } finally {
      setImportLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getCountFromServer(query(collection(db, 'Customers')));
        if (!cancelled) setTotalCustomers(snap.data().count);
      } catch (err) {
        console.error('Customer total failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const term = searchTerm.trim();
    const browsingSms = smsFilter === 'opted-in' || smsFilter === 'opted-out';
    const browsingTier = tierFilter !== 'all';
    const browsingBirthday = birthdayFilter === 'saved';
    if (!term && !browsingSms && !browsingTier && !browsingBirthday) {
      searchRequestIdRef.current += 1;
      setCustomers([]);
      setFilteredCustomers([]);
      setSearching(false);
      setSearchError('');
      setHasSearched(false);
      setBrowseCounts(null);
      setBrowseCapped(false);
      setCurrentPage(0);
      return undefined;
    }

    if (!term) {
      const requestId = ++searchRequestIdRef.current;
      let cancelled = false;
      const scope = [];
      if (browsingTier) scope.push(where('rewardsTier', '==', tierFilter));
      const listConstraints = [...scope];
      if (smsFilter === 'opted-in') listConstraints.push(where('smsSubscribed', '==', true));
      if (smsFilter === 'opted-out') listConstraints.push(where('smsSubscribed', '==', false));
      if (browsingBirthday) listConstraints.push(where('birthdayMonth', '>=', 1));
      const listLimit = browsingSms || browsingBirthday ? SMS_BROWSE_LIMIT : TIER_BROWSE_LIMIT;

      const countWhere = async (extra) => {
        const snap = await getCountFromServer(
          query(collection(db, 'Customers'), ...scope, ...extra)
        );
        return snap.data().count;
      };

      (async () => {
        setSearching(true);
        setSearchError('');
        try {
          let snap;
          try {
            snap = await getDocs(
              query(collection(db, 'Customers'), ...listConstraints, limit(listLimit))
            );
          } catch (listErr) {
            const fallback = browsingBirthday
              ? [where('birthdayMonth', '>=', 1), limit(listLimit)]
              : browsingSms
                ? [where('smsSubscribed', '==', smsFilter === 'opted-in'), limit(listLimit)]
                : [where('rewardsTier', '==', tierFilter), limit(listLimit)];
            snap = await getDocs(query(collection(db, 'Customers'), ...fallback));
          }
          if (cancelled || requestId !== searchRequestIdRef.current) return;
          const results = snap.docs.map((customerDoc) => ({ id: customerDoc.id, ...customerDoc.data() }));
          results.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
          setCustomers(results);
          setBrowseCapped(snap.size >= listLimit);
          setHasSearched(true);
          setCurrentPage(0);

          try {
            const [optedIn, notOptedIn] = await Promise.all([
              countWhere([where('smsSubscribed', '==', true)]),
              countWhere([where('smsSubscribed', '==', false)]),
            ]);
            if (cancelled || requestId !== searchRequestIdRef.current) return;
            setBrowseCounts({
              total: optedIn + notOptedIn,
              optedIn,
              notOptedIn,
            });
          } catch (countErr) {
            console.error('Customer count failed:', countErr);
            if (cancelled || requestId !== searchRequestIdRef.current) return;
            const optedIn = results.filter((customer) => customer.smsSubscribed === true).length;
            setBrowseCounts({
              total: results.length,
              optedIn,
              notOptedIn: results.length - optedIn,
            });
          }
        } catch (err) {
          console.error('Customer browse failed:', err);
          if (cancelled || requestId !== searchRequestIdRef.current) return;
          setCustomers([]);
          setBrowseCounts(null);
          setBrowseCapped(false);
          setSearchError(err?.message || 'Could not load customers.');
          setHasSearched(true);
        } finally {
          if (!cancelled && requestId === searchRequestIdRef.current) setSearching(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    const phoneDigits = normalizePhone(term);
    const hasLetters = /[a-zA-Z]/.test(term);
    const tooShortPhone =
      phoneDigits.length > 0 &&
      phoneDigits.length < MIN_PHONE_PREFIX_LEN &&
      !hasLetters;
    const tooShortName = hasLetters && term.length < MIN_NAME_SEARCH_LEN;

    if (tooShortPhone || tooShortName) {
      searchRequestIdRef.current += 1;
      setCustomers([]);
      setFilteredCustomers([]);
      setSearching(false);
      setHasSearched(true);
      setSearchError(
        tooShortPhone
          ? `Enter at least ${MIN_PHONE_PREFIX_LEN} digits (or the full 10-digit phone).`
          : `Enter at least ${MIN_NAME_SEARCH_LEN} characters to search by name.`
      );
      return undefined;
    }

    const requestId = ++searchRequestIdRef.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError('');
      try {
        const results = await searchCustomersInFirestore(term);
        if (requestId !== searchRequestIdRef.current) return;
        setCustomers(results);
        setBrowseCounts(null);
        setBrowseCapped(false);
        setHasSearched(true);
        setCurrentPage(0);
      } catch (err) {
        console.error('Customer search failed:', err);
        if (requestId !== searchRequestIdRef.current) return;
        setCustomers([]);
        setSearchError(err?.message || 'Search failed. Please try again.');
        setHasSearched(true);
      } finally {
        if (requestId === searchRequestIdRef.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm, tierFilter, smsFilter, birthdayFilter]);

  // Load SMS opt-out registry only when that filter is selected (not on every page visit)
  useEffect(() => {
    if (smsFilter !== 'sms-opt-out-registry' || smsOptOutsLoaded) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'SMSOptOuts'));
        if (cancelled) return;
        const seen = new Set();
        snap.forEach((d) => {
          const data = d.data();
          if (data?.optedOut !== true) return;
          const key = normalizePhone(String(d.id || ''));
          if (key) seen.add(key);
        });
        setSmsOptOutPhones(Array.from(seen));
        setSmsOptOutsLoaded(true);
      } catch (err) {
        console.error('Error loading SMSOptOuts:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [smsFilter, smsOptOutsLoaded]);

  // Handle URL search parameter (and open account when search is an exact phone / barcode)
  useEffect(() => {
    const urlSearchTerm = searchParams.get('search');
    if (urlSearchTerm) {
      setSearchTerm(urlSearchTerm);
    }
  }, [searchParams]);

  // Auto-open customer when search is an exact 10-digit phone (barcode scan or ?search=)
  useEffect(() => {
    if (searching || customers.length === 0) return;
    const phone10 = normalizePhone(searchTerm);
    if (phone10.length !== 10) {
      autoOpenedPhoneRef.current = '';
      return;
    }
    if (autoOpenedPhoneRef.current === phone10) return;
    const match = findCustomerByPhone(phone10, customers);
    if (match) {
      autoOpenedPhoneRef.current = phone10;
      handleCustomerClick(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per exact phone value
  }, [searching, customers, searchTerm]);

  // Filter search results by SMS / orders (never loads the full customer list)
  useEffect(() => {
    let filtered = [...customers];

    if (smsFilter === 'opted-in') {
      filtered = filtered.filter((customer) => customer.smsSubscribed === true);
    } else if (smsFilter === 'opted-out') {
      filtered = filtered.filter((customer) => customer.smsSubscribed !== true);
    } else if (smsFilter === 'sms-opt-out-registry') {
      const optSet = new Set(smsOptOutPhones);
      filtered = filtered.filter((customer) => {
        const key = normalizePhone(String(customer.id || ''));
        return key && optSet.has(key);
      });
    }

    if (ordersFilter === 'with-orders') {
      filtered = filtered.filter((c) => customerHasOrdersInProfile(c));
    } else if (ordersFilter === 'without-orders') {
      filtered = filtered.filter((c) => !customerHasOrdersInProfile(c));
    }

    if (tierFilter !== 'all') {
      filtered = filtered.filter(
        (customer) => customerRewardsTierId(customer) === tierFilter
      );
    }

    if (birthdayFilter === 'saved') {
      filtered = filtered.filter(customerHasSavedBirthday);
    } else if (birthdayFilter === 'missing') {
      filtered = filtered.filter((customer) => !customerHasSavedBirthday(customer));
    }

    setFilteredCustomers(filtered);
    setCurrentPage(0);
  }, [smsFilter, ordersFilter, tierFilter, birthdayFilter, customers, smsOptOutPhones]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);
  const startIndex = currentPage * customersPerPage;
  const endIndex = startIndex + customersPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  // Pagination functions
  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToFirstPage = () => {
    setCurrentPage(0);
  };

  const goToLastPage = () => {
    setCurrentPage(totalPages - 1);
  };

  function orderMarkedForAlteration(orderData) {
    const items = orderData?.line_items;
    if (!Array.isArray(items)) return false;
    return items.some((item) => item && item.needsAlteration === true);
  }

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    // Remove any non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone; // Return original if can't format
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCustomerClick = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerAlterations(customer.alterations || {});
    setTierRepairMessage('');
    setTierRepairError('');
    setShowModal(true);
    
    // Fetch order statuses from Orders collection
    if (customer.orders && customer.orders.length > 0) {
      const statusMap = {};
      for (const order of customer.orders) {
        try {
          const orderRef = doc(db, 'Orders', order.orderNumber);
          const orderDoc = await getDoc(orderRef);
          if (orderDoc.exists()) {
            const orderData = orderDoc.data();
            statusMap[order.orderNumber] = orderMarkedForAlteration(orderData)
              ? (orderData.status || 'order created')
              : '';
          }
        } catch (error) {
          console.error(`Error fetching status for order ${order.orderNumber}:`, error);
        }
      }
      setOrdersStatusMap(statusMap);
    }
  };

  const openCustomerByPhone = async (phoneRaw) => {
    const phone10 = normalizePhone(phoneRaw);
    if (phone10.length !== 10) return false;
    let match = findCustomerByPhone(phone10, customers);
    if (!match) {
      try {
        const snap = await getDoc(doc(db, 'Customers', phone10));
        if (snap.exists()) match = { id: snap.id, ...snap.data() };
      } catch (err) {
        console.error('Open by phone failed:', err);
        return false;
      }
    }
    if (!match) return false;
    await handleCustomerClick(match);
    return true;
  };

  const handleCustomerSearchKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const phone10 = normalizePhone(searchTerm);
    if (phone10.length === 10) {
      autoOpenedPhoneRef.current = '';
      await openCustomerByPhone(searchTerm);
    }
  };

  // Set up real-time listeners for order statuses when a customer is selected
  useEffect(() => {
    if (!selectedCustomer || !selectedCustomer.orders || selectedCustomer.orders.length === 0) {
      return;
    }

    const unsubscribeFunctions = [];

    selectedCustomer.orders.forEach((order) => {
      const orderRef = doc(db, 'Orders', order.orderNumber);
      const unsubscribe = onSnapshot(orderRef, (orderDoc) => {
        if (orderDoc.exists()) {
          const orderData = orderDoc.data();
          setOrdersStatusMap(prev => ({
            ...prev,
            [order.orderNumber]: orderMarkedForAlteration(orderData)
              ? (orderData.status || 'order created')
              : ''
          }));
        }
      }, (error) => {
        console.error(`Error listening to order ${order.orderNumber}:`, error);
      });
      unsubscribeFunctions.push(unsubscribe);
    });

    // Cleanup function
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [selectedCustomer]);

  const closeModal = () => {
    setShowModal(false);
    setSelectedCustomer(null);
    setExpandedOrders({});
    setOrderHistoryExpanded(false);
    setAlterationsExpanded(false);
    setCustomerAlterations({});
    setOrdersStatusMap({});
    setPendingCustomerChanges({});
  };

  const toggleOrderExpansion = (orderNumber) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderNumber]: !prev[orderNumber]
    }));
  };

  const updateCustomerAlteration = (field, value) => {
    setCustomerAlterations(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveCustomerAlterations = async () => {
    if (!selectedCustomer) return;
    
    setSavingAlterations(true);
    try {
      const customerRef = doc(db, 'Customers', selectedCustomer.id);
      await setDoc(customerRef, {
        ...selectedCustomer,
        alterations: customerAlterations
      }, { merge: true });
      
      // Update local state
      setSelectedCustomer(prev => ({
        ...prev,
        alterations: customerAlterations
      }));
      
      alert('Customer alterations saved successfully!');
    } catch (error) {
      console.error('Error saving alterations:', error);
      alert('Error saving alterations. Please try again.');
    } finally {
      setSavingAlterations(false);
    }
  };

  const saveCustomerChanges = async () => {
    if (!selectedCustomer || Object.keys(pendingCustomerChanges).length === 0) {
      return;
    }

    setSavingCustomerChanges(true);
    try {
      const customerRef = doc(db, 'Customers', selectedCustomer.id);
      const updateData = { ...pendingCustomerChanges };
      if (updateData.birthdayMonth || updateData.birthdayDay) {
        if (updateData.birthdayMonth) updateData.birthdayMonth = Number(updateData.birthdayMonth);
        if (updateData.birthdayDay) updateData.birthdayDay = Number(updateData.birthdayDay);
        if (!selectedCustomer.birthdaySavedAt) {
          updateData.birthdaySavedAt = serverTimestamp();
        }
      }

      // If smsSubscribed is being set to true, also clear opt-out
      if (updateData.smsSubscribed === true && selectedCustomer.phone) {
        const normalizedPhone = selectedCustomer.phone.replace(/\D/g, '');
        const optOutRef = doc(db, 'SMSOptOuts', normalizedPhone);
        await setDoc(optOutRef, {
          phoneNumber: `+1${normalizedPhone}`,
          optedOut: false,
          updatedAt: new Date().toISOString()
        }, { merge: true });
      }

      // Update customer document
      await updateDoc(customerRef, updateData);

      // Also update all orders for this customer if smsSubscribed changed
      if (updateData.smsSubscribed !== undefined && selectedCustomer.phone) {
        const normalizedPhone = selectedCustomer.phone.replace(/\D/g, '');
        const ordersRef = collection(db, 'Orders');
        const ordersQuery = query(ordersRef, where('phone', '==', normalizedPhone));
        const ordersSnapshot = await getDocs(ordersQuery);
        
        if (!ordersSnapshot.empty) {
          const batch = writeBatch(db);
          ordersSnapshot.forEach((orderDoc) => {
            batch.update(orderDoc.ref, { smsSubscribed: updateData.smsSubscribed });
          });
          await batch.commit();
        }
      }

      // Update local state
      setSelectedCustomer(prev => ({
        ...prev,
        ...updateData
      }));

      setPendingCustomerChanges({});
      alert(`✅ Successfully saved customer changes!`);
    } catch (error) {
      console.error('Error saving customer changes:', error);
      alert('❌ Failed to save changes. Please try again.');
    } finally {
      setSavingCustomerChanges(false);
    }
  };

  const tierCohort = tierFilter === 'all'
    ? customers
    : customers.filter(
        (customer) => customerRewardsTierId(customer) === tierFilter
      );
  const optedInCount = tierCohort.filter((customer) => customer.smsSubscribed === true).length;
  const notOptedInCount = tierCohort.length - optedInCount;
  const summary = !searchTerm.trim() && browseCounts
    ? browseCounts
    : {
        total: tierCohort.length,
        optedIn: optedInCount,
        notOptedIn: notOptedInCount,
      };

  const noCustomersMessage = (() => {
    if (searchError) return searchError;
    if (
      !searchTerm.trim() &&
      tierFilter === 'all' &&
      smsFilter !== 'opted-in' &&
      smsFilter !== 'opted-out' &&
      birthdayFilter !== 'saved'
    ) {
      return 'Search by phone or name, or choose Opted in, Not opted in, a rewards tier, or Has birthday.';
    }
    if (!searchTerm.trim() && searching) return 'Loading customers…';
    if (searching) return 'Searching…';
    if (hasSearched && customers.length > 0 && filteredCustomers.length === 0) {
      return 'No customers match your filters';
    }
    if (hasSearched) return 'No customers match your search';
    return 'Search by phone or name to find a customer.';
  })();

  return (
    <div className="customers-container">
      <div className="customers-header">
        <div className="customers-header-content">
          <div className="customers-title-section">
            <h1>Customers</h1>
            <p>
              {totalCustomers == null
                ? 'Counting customers…'
                : `${totalCustomers.toLocaleString()} total customers`}
            </p>
          </div>
          <div className="customers-header-actions">
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              style={{ display: 'none' }}
              id="customer-csv-input"
            />
            <button
              type="button"
              className="import-csv-btn"
              onClick={() => csvInputRef.current?.click()}
              disabled={importLoading}
            >
              {importLoading ? 'Importing…' : 'Import CSV'}
            </button>
            <button
              type="button"
              className="transfer-points-btn"
              onClick={openTransferModal}
            >
              Transfer points
            </button>
            <button
              type="button"
              className="transfer-points-btn rewards-audit-btn"
              onClick={openAuditModal}
            >
              Rewards audit
            </button>
          </div>
          <div className="filters-container">
          <div className="search-container">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name or phone (scan customer barcode)…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleCustomerSearchKeyDown}
              className="search-input"
              autoFocus
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  autoOpenedPhoneRef.current = '';
                }}
                className="clear-search-btn"
                title="Clear search"
              >
                ×
              </button>
            )}
            </div>
            <div className="sms-filter-container">
              <label htmlFor="tier-filter" className="sms-filter-label">Tier:</label>
              <select
                id="tier-filter"
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="sms-filter-select"
              >
                <option value="all">All tiers</option>
                <option value="diamond">Diamond</option>
                <option value="gold">Gold</option>
                <option value="silver">Silver</option>
              </select>
            </div>
            <div className="sms-filter-container">
              <label htmlFor="sms-filter" className="sms-filter-label">SMS:</label>
              <select
                id="sms-filter"
                value={smsFilter}
                onChange={(e) => setSmsFilter(e.target.value)}
                className="sms-filter-select"
                title="Show all customers in this SMS group, with or without a search"
              >
                <option value="all">All</option>
                <option value="opted-in">Opted in</option>
                <option value="opted-out">Not opted in</option>
                <option value="sms-opt-out-registry">SMS Opt-Outs registry</option>
              </select>
            </div>
            <div className="sms-filter-container">
              <label htmlFor="birthday-filter" className="sms-filter-label">Birthday:</label>
              <select
                id="birthday-filter"
                value={birthdayFilter}
                onChange={(e) => setBirthdayFilter(e.target.value)}
                className="sms-filter-select"
                title="Has birthday lists customers who saved a date. No birthday filters the list you already have."
              >
                <option value="all">All</option>
                <option value="saved">Has birthday</option>
                <option value="missing">No birthday</option>
              </select>
            </div>
            <div className="sms-filter-container">
              <label htmlFor="orders-filter" className="sms-filter-label">Orders:</label>
              <select
                id="orders-filter"
                value={ordersFilter}
                onChange={(e) => setOrdersFilter(e.target.value)}
                className="sms-filter-select"
                disabled={
                  !hasSearched &&
                  !searchTerm.trim() &&
                  tierFilter === 'all' &&
                  smsFilter !== 'opted-in' &&
                  smsFilter !== 'opted-out' &&
                  birthdayFilter !== 'saved'
                }
                title="Filters apply to the customers currently listed"
              >
                <option value="all">All</option>
                <option value="with-orders">With orders</option>
                <option value="without-orders">Without orders</option>
              </select>
            </div>
            <div className="customer-count-summary" aria-live="polite">
              <span>
                <strong>{totalCustomers == null ? '…' : totalCustomers.toLocaleString()}</strong> total
              </span>
              {hasSearched && !searching && (
                <>
                  <span><strong>{summary.total}</strong> in this view</span>
                  <span><strong>{summary.optedIn}</strong> opted in</span>
                  <span><strong>{summary.notOptedIn}</strong> not opted in</span>
                </>
              )}
            </div>
          </div>
        </div>
        {(hasSearched || searching) && (
          <div className="search-results-info">
            {searching ? 'Searching…' : `Showing ${filteredCustomers.length} result${filteredCustomers.length === 1 ? '' : 's'}`}
            {tierFilter !== 'all' && (
              <span className="filter-badge">
                {tierFilter === 'diamond' && 'Diamond'}
                {tierFilter === 'gold' && 'Gold'}
                {tierFilter === 'silver' && 'Silver'}
              </span>
            )}
            {smsFilter !== 'all' && (
              <span className="filter-badge">
                {smsFilter === 'opted-in' && 'Opted in'}
                {smsFilter === 'opted-out' && 'Not opted in'}
                {smsFilter === 'sms-opt-out-registry' && 'SMS Opt-Outs registry'}
              </span>
            )}
            {birthdayFilter !== 'all' && (
              <span className="filter-badge">
                {birthdayFilter === 'saved' && 'Has birthday'}
                {birthdayFilter === 'missing' && 'No birthday'}
              </span>
            )}
            {ordersFilter !== 'all' && (
              <span className="filter-badge">
                {ordersFilter === 'with-orders' && 'With orders'}
                {ordersFilter === 'without-orders' && 'Without orders'}
              </span>
            )}
            {browseCapped && (
              <span className="filter-badge">
                First {smsFilter === 'opted-in' || smsFilter === 'opted-out' || birthdayFilter === 'saved' ? SMS_BROWSE_LIMIT : TIER_BROWSE_LIMIT}
              </span>
            )}
          </div>
        )}
        {importResult && (
          <div className="import-result">
            <span className="import-result-summary">
              Import: {importResult.imported} added, {importResult.updated} updated.
              {importResult.errors?.length ? ` ${importResult.errors.length} row(s) had issues.` : ''}
            </span>
            {importResult.errors?.length > 0 && (
              <ul className="import-result-errors">
                {importResult.errors.slice(0, 5).map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
                {importResult.errors.length > 5 && (
                  <li>… and {importResult.errors.length - 5} more</li>
                )}
              </ul>
            )}
            <button type="button" className="import-result-dismiss" onClick={() => setImportResult(null)}>Dismiss</button>
          </div>
        )}
      </div>

      <div className="customers-list">
        {currentCustomers.length === 0 ? (
          <div className="no-customers">
            <p>{noCustomersMessage}</p>
          </div>
        ) : (
          currentCustomers.map((customer) => {
            const loyalty = getCustomerLoyaltyTier(customer);
            const points = getCustomerLoyaltyPoints(customer);
            return (
            <div key={customer.id} className="customer-card" onClick={() => handleCustomerClick(customer)}>
              <div className="customer-info">
                <div className="customer-name-row">
                  <div className="customer-name">{customer.name || 'Unknown Customer'}</div>
                  <span className={`customer-loyalty-badge customer-loyalty-badge-${loyalty.id}`}>
                    {loyalty.name}
                  </span>
                </div>
                <div className="customer-details">
                  <div className="customer-phone">{formatPhoneNumber(customer.phone)}</div>
                  <div className="customer-email">{customer.email || 'No email'}</div>
                </div>
              </div>
              <div className="customer-stats">
                <div className="customer-stats-left">
                  <div className="order-count">
                    {customer.orders ? customer.orders.length : 0} orders
                  </div>
                  <div className="customer-points">
                    {points.toLocaleString()} pts
                  </div>
                </div>
                <div className="last-order">
                  Last order: {customer.lastOrderDate ? formatDate(customer.lastOrderDate) : 'N/A'}
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* Pagination Controls */}
      {filteredCustomers.length > customersPerPage && (
        <div className="pagination-controls">
          <button 
            className="nav-btn nav-btn-first" 
            onClick={goToFirstPage}
            disabled={currentPage === 0}
          >
            First
          </button>
          <button 
            className="nav-btn nav-btn-prev" 
            onClick={goToPreviousPage}
            disabled={currentPage === 0}
          >
            Previous
          </button>
          <div className="page-info">
            Page {currentPage + 1} of {totalPages}
          </div>
          <button 
            className="nav-btn nav-btn-next" 
            onClick={goToNextPage}
            disabled={currentPage === totalPages - 1}
          >
            Next
          </button>
          <button 
            className="nav-btn nav-btn-last" 
            onClick={goToLastPage}
            disabled={currentPage === totalPages - 1}
          >
            Last
          </button>
        </div>
      )}

      {/* Customer Details Modal */}
      {showModal && selectedCustomer && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Customer Details</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="customer-profile">
                <div className="profile-section">
                  <h3>Contact Information</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Name:</label>
                      <span>{selectedCustomer.name || 'Unknown'}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone:</label>
                      <span>{formatPhoneNumber(selectedCustomer.phone)}</span>
                    </div>
                    <div className="info-item">
                      <label>Email:</label>
                      <span>{selectedCustomer.email || 'No email provided'}</span>
                    </div>
                    <div className="info-item">
                      <label>Total Orders:</label>
                      <span>{selectedCustomer.orders ? selectedCustomer.orders.length : 0}</span>
                    </div>
                    <div className="info-item">
                      <label>Loyalty status:</label>
                      <span className={`customer-loyalty-badge customer-loyalty-badge-${getCustomerLoyaltyTier(selectedCustomer).id}`}>
                        {getCustomerLoyaltyTier(selectedCustomer).name}
                      </span>
                    </div>
                    <div className="info-item">
                      <label>Yearly spend ({new Date().getFullYear()}):</label>
                      <span>
                        {getCustomerYearlySpend(selectedCustomer).toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })}
                      </span>
                    </div>
                    <div className="info-item">
                      <label>Rewards points:</label>
                      <span>{getCustomerLoyaltyPoints(selectedCustomer).toLocaleString()}</span>
                    </div>
                    <div className="info-item info-item-full">
                      <label>Birthday:</label>
                      <span>
                        {(() => {
                          const month = Number(
                            pendingCustomerChanges.birthdayMonth ?? selectedCustomer.birthdayMonth
                          );
                          const day = Number(
                            pendingCustomerChanges.birthdayDay ?? selectedCustomer.birthdayDay
                          );
                          if (month >= 1 && day >= 1) {
                            return new Date(Date.UTC(2000, month - 1, day)).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              timeZone: 'UTC',
                            });
                          }
                          return 'Not saved';
                        })()}
                      </span>
                      <div className="customer-birthday-edit">
                        <select
                          value={
                            pendingCustomerChanges.birthdayMonth ??
                            selectedCustomer.birthdayMonth ??
                            ''
                          }
                          onChange={(e) => {
                            const value = e.target.value ? Number(e.target.value) : '';
                            setPendingCustomerChanges((prev) => ({ ...prev, birthdayMonth: value }));
                          }}
                          aria-label="Birth month"
                        >
                          <option value="">Month</option>
                          {[
                            'January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December',
                          ].map((label, i) => (
                            <option key={label} value={i + 1}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <select
                          value={
                            pendingCustomerChanges.birthdayDay ?? selectedCustomer.birthdayDay ?? ''
                          }
                          onChange={(e) => {
                            const value = e.target.value ? Number(e.target.value) : '';
                            setPendingCustomerChanges((prev) => ({ ...prev, birthdayDay: value }));
                          }}
                          aria-label="Birth day"
                        >
                          <option value="">Day</option>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="info-item info-item-full">
                      <label>Active tier rewards:</label>
                      <div className="customer-tier-benefits">
                        {getActiveTierBenefitsForCustomer(selectedCustomer).length > 0 ? (
                          <ul>
                            {getActiveTierBenefitsForCustomer(selectedCustomer).map((b) => (
                              <li key={b.id}>
                                <strong>{b.title}</strong>
                                <span>{b.description}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="customer-tier-benefits-empty">
                            Silver — no automatic Shopify discounts. Gold/Diamond unlock checkout perks.
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="info-item">
                      <label>Shopify customer ID:</label>
                      <span>{selectedCustomer.shopifyId || selectedCustomer.shopifyCustomerId || 'Not linked'}</span>
                    </div>
                    <div className="info-item">
                      <label>Shopify tier tag:</label>
                      <span>
                        {getExpectedShopifyTierTag(selectedCustomer) || 'none (Silver)'}
                        {selectedCustomer.rewardsTierShopifySynced
                          ? ` · synced as ${selectedCustomer.rewardsTierShopifySynced}`
                          : ' · not synced yet'}
                      </span>
                    </div>
                    <div className="info-item info-item-full customer-tier-repair-row">
                      <button
                        type="button"
                        className="customer-tier-repair-btn"
                        onClick={handleRepairTierTag}
                        disabled={tierRepairLoading}
                      >
                        {tierRepairLoading ? 'Syncing…' : 'Repair Shopify tier tag'}
                      </button>
                      {tierRepairMessage ? (
                        <p className="customer-tier-repair-ok">{tierRepairMessage}</p>
                      ) : null}
                      {tierRepairError ? (
                        <p className="customer-tier-repair-err">{tierRepairError}</p>
                      ) : null}
                    </div>
                    <div className="info-item info-item-sms-row">
                      <label>SMS Subscribed:</label>
                      <label className="sms-checkbox-label">
                        <input
                          type="checkbox"
                          checked={pendingCustomerChanges.smsSubscribed !== undefined 
                            ? pendingCustomerChanges.smsSubscribed 
                            : (selectedCustomer.smsSubscribed || false)}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setPendingCustomerChanges(prev => ({
                              ...prev,
                              smsSubscribed: newValue
                            }));
                            setSelectedCustomer(prev => ({
                              ...prev,
                              smsSubscribed: newValue
                            }));
                          }}
                          className="sms-subscription-checkbox"
                        />
                        <span>Customer has opted in to receive SMS notifications</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="marketing-sms-log-section">
                  <h3>Marketing SMS log</h3>
                  {Array.isArray(selectedCustomer.marketingSmsSentLog) &&
                  selectedCustomer.marketingSmsSentLog.length > 0 ? (
                    <ul className="marketing-sms-log-list">
                      {[...selectedCustomer.marketingSmsSentLog]
                        .sort((a, b) => {
                          const ta = a?.sentAt?.toDate
                            ? a.sentAt.toDate().getTime()
                            : new Date(a?.sentAt || 0).getTime();
                          const tb = b?.sentAt?.toDate
                            ? b.sentAt.toDate().getTime()
                            : new Date(b?.sentAt || 0).getTime();
                          return tb - ta;
                        })
                        .map((entry, idx) => (
                          <li key={idx}>
                            {formatDate(entry.sentAt)}
                            {entry.discountCode ? (
                              <span className="marketing-sms-log-code"> — Code: {entry.discountCode}</span>
                            ) : null}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className="marketing-sms-log-empty">No marketing messages logged yet.</p>
                  )}
                </div>

                <div className="orders-section">
                  <button
                    type="button"
                    className="section-collapse-header"
                    onClick={() => setOrderHistoryExpanded((v) => !v)}
                    aria-expanded={orderHistoryExpanded}
                  >
                    <h3>Order History</h3>
                    <span className="section-expand-icon" aria-hidden="true">
                      {orderHistoryExpanded ? '▼' : '▶'}
                    </span>
                  </button>
                  {orderHistoryExpanded && (
                    selectedCustomer.orders && selectedCustomer.orders.length > 0 ? (
                      <div className="orders-list">
                        {selectedCustomer.orders.map((order, index) => (
                          <div key={index} className="order-item">
                            <div
                              className="order-header expandable-header"
                              onClick={() => toggleOrderExpansion(order.orderNumber)}
                            >
                              <div className="order-header-info">
                                <span className="order-number">Order {order.orderNumber}</span>
                                <div className="order-meta">
                                  <span className="order-date">{formatDate(order.timestamp)}</span>
                                  {ordersStatusMap[order.orderNumber] && (
                                    <span className={`order-status status-${(ordersStatusMap[order.orderNumber] || 'order created').replace(/\s+/g, '-')}`}>
                                      {ordersStatusMap[order.orderNumber].split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="expand-icon">
                                {expandedOrders[order.orderNumber] ? '▼' : '▶'}
                              </div>
                            </div>
                            {expandedOrders[order.orderNumber] && (
                              <div className="order-items">
                                {order.line_items && order.line_items.length > 0 ? (
                                  <div className="items-list">
                                    {order.line_items.map((item, itemIndex) => (
                                      <div key={itemIndex} className="item-detail">
                                        <span className="item-name">{item.name}</span>
                                        <span className="item-quantity">Qty: {item.quantity}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="no-items">No items found</div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-orders">No orders found</div>
                    )
                  )}
                </div>

                <div className="alterations-section">
                  <button
                    type="button"
                    className="section-collapse-header"
                    onClick={() => setAlterationsExpanded((v) => !v)}
                    aria-expanded={alterationsExpanded}
                  >
                    <h3>Customer Alteration Measurements</h3>
                    <span className="section-expand-icon" aria-hidden="true">
                      {alterationsExpanded ? '▼' : '▶'}
                    </span>
                  </button>

                  {alterationsExpanded && (
                  <>
                  {/* Jacket Measurements Section */}
                  <div className="measurement-group">
                    <h4 className="measurement-group-title">Jacket Measurements</h4>
                    <div className="measurement-grid">
                      <div className="measurement-field">
                        <label className="measurement-label">Back Length</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.backLength || ''}
                          onChange={(e) => updateCustomerAlteration('backLength', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Shoulder Width</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shoulderWidth || ''}
                          onChange={(e) => updateCustomerAlteration('shoulderWidth', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Outsleeve L</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.outsleeveL || ''}
                          onChange={(e) => updateCustomerAlteration('outsleeveL', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Outsleeve R</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.outsleeveR || ''}
                          onChange={(e) => updateCustomerAlteration('outsleeveR', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Chest</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.chest || ''}
                          onChange={(e) => updateCustomerAlteration('chest', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Stomach</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.stomach || ''}
                          onChange={(e) => updateCustomerAlteration('stomach', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Seat</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.seat || ''}
                          onChange={(e) => updateCustomerAlteration('seat', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Upperarm</label>
                      <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.upperarm || ''}
                          onChange={(e) => updateCustomerAlteration('upperarm', e.target.value)}
                        placeholder="Enter measurement"
                          step="1"
                          min="0"
                      />
                      </div>
                    </div>
                  </div>

                  {/* Trouser Measurements Section */}
                  <div className="measurement-group">
                    <h4 className="measurement-group-title">Trouser Measurements</h4>
                    <div className="measurement-grid">
                      <div className="measurement-field">
                        <label className="measurement-label">Waist</label>
                      <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.waist || ''}
                          onChange={(e) => updateCustomerAlteration('waist', e.target.value)}
                        placeholder="Enter measurement"
                          step="1"
                          min="0"
                      />
                    </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Seat</label>
                      <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.trouserSeat || ''}
                          onChange={(e) => updateCustomerAlteration('trouserSeat', e.target.value)}
                        placeholder="Enter measurement"
                          step="1"
                          min="0"
                      />
                    </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Outseam L</label>
                      <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.outseamL || ''}
                          onChange={(e) => updateCustomerAlteration('outseamL', e.target.value)}
                        placeholder="Enter measurement"
                          step="1"
                          min="0"
                      />
                    </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Outseam R</label>
                      <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.outseamR || ''}
                          onChange={(e) => updateCustomerAlteration('outseamR', e.target.value)}
                        placeholder="Enter measurement"
                          step="1"
                          min="0"
                      />
                    </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Crotch</label>
                      <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.crotch || ''}
                          onChange={(e) => updateCustomerAlteration('crotch', e.target.value)}
                        placeholder="Enter measurement"
                          step="1"
                          min="0"
                      />
                    </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Knee</label>
                      <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.knee || ''}
                          onChange={(e) => updateCustomerAlteration('knee', e.target.value)}
                        placeholder="Enter measurement"
                          step="1"
                          min="0"
                      />
                    </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Foot</label>
                      <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.foot || ''}
                          onChange={(e) => updateCustomerAlteration('foot', e.target.value)}
                        placeholder="Enter measurement"
                          step="1"
                          min="0"
                      />
                    </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Rise</label>
                      <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.rise || ''}
                          onChange={(e) => updateCustomerAlteration('rise', e.target.value)}
                        placeholder="Enter measurement"
                          step="1"
                          min="0"
                      />
                      </div>
                    </div>
                  </div>

                  {/* Shirt Measurements Section */}
                  <div className="measurement-group">
                    <h4 className="measurement-group-title">Shirt Measurements</h4>
                    <div className="measurement-grid">
                      <div className="measurement-field">
                        <label className="measurement-label">Neck</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtNeck || ''}
                          onChange={(e) => updateCustomerAlteration('shirtNeck', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Chest</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtChest || ''}
                          onChange={(e) => updateCustomerAlteration('shirtChest', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Waist</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtWaist || ''}
                          onChange={(e) => updateCustomerAlteration('shirtWaist', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Seat/Hips</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtSeatHips || ''}
                          onChange={(e) => updateCustomerAlteration('shirtSeatHips', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Shoulders</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtShoulders || ''}
                          onChange={(e) => updateCustomerAlteration('shirtShoulders', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Sleeve Length</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtSleeveLength || ''}
                          onChange={(e) => updateCustomerAlteration('shirtSleeveLength', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Biceps/Upper Arm</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtBicepsUpperArm || ''}
                          onChange={(e) => updateCustomerAlteration('shirtBicepsUpperArm', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Cuff</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtCuff || ''}
                          onChange={(e) => updateCustomerAlteration('shirtCuff', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Shirt Length (Back)</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtLengthBack || ''}
                          onChange={(e) => updateCustomerAlteration('shirtLengthBack', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Yoke Width</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtYokeWidth || ''}
                          onChange={(e) => updateCustomerAlteration('shirtYokeWidth', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Front Length</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtFrontLength || ''}
                          onChange={(e) => updateCustomerAlteration('shirtFrontLength', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                      <div className="measurement-field">
                        <label className="measurement-label">Collar Spread</label>
                        <input
                          type="number"
                          className="measurement-input"
                          value={customerAlterations.shirtCollarSpread || ''}
                          onChange={(e) => updateCustomerAlteration('shirtCollarSpread', e.target.value)}
                          placeholder="Enter measurement"
                          step="1"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="alteration-actions">
                    <button 
                      className="save-alterations-btn"
                      onClick={saveCustomerAlterations}
                      disabled={savingAlterations}
                    >
                      {savingAlterations ? 'Saving...' : 'Save Alterations'}
                    </button>
                  </div>
                  </>
                  )}
                </div>
              </div>

              {/* Modal Footer with Save Button for Customer Changes */}
              {Object.keys(pendingCustomerChanges).length > 0 && (
                <div className="modal-footer">
                  <div className="pending-changes-indicator">
                    <span className="pending-changes-text">
                      {Object.keys(pendingCustomerChanges).length} pending change(s)
                    </span>
                  </div>
                  <div className="modal-buttons">
                    <button 
                      className="modal-btn save-btn" 
                      onClick={saveCustomerChanges}
                      disabled={savingCustomerChanges}
                    >
                      {savingCustomerChanges ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button 
                      className="modal-btn" 
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showTransferModal && (
        <div className="modal-overlay" onClick={closeTransferModal}>
          <div
            className="modal-content transfer-points-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Transfer rewards points</h2>
              <button
                type="button"
                className="close-btn"
                onClick={closeTransferModal}
                disabled={transferLoading}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form className="transfer-points-form" onSubmit={handleTransferPoints}>
              <p className="transfer-points-desc">
                Move points from one customer account to another using their phone numbers.
              </p>

              <div className="transfer-field">
                <label htmlFor="transfer-from-phone">From phone</label>
                <input
                  id="transfer-from-phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="10-digit phone number"
                  value={transferFromPhone}
                  onChange={(e) => {
                    setTransferFromPhone(e.target.value);
                    setTransferError('');
                    setTransferSuccess('');
                  }}
                  onBlur={() => refreshTransferPreview('from', transferFromPhone)}
                  disabled={transferLoading}
                />
                {transferFromPreview && (
                  <p className={`transfer-preview ${transferFromPreview.missing ? 'is-missing' : ''}`}>
                    {transferFromPreview.missing
                      ? `No customer found for ${transferFromPreview.phone}`
                      : `${transferFromPreview.name} · ${transferFromPreview.tier} · ${transferFromPreview.points.toLocaleString()} pts`}
                  </p>
                )}
              </div>

              <div className="transfer-field">
                <label htmlFor="transfer-to-phone">To phone</label>
                <input
                  id="transfer-to-phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="10-digit phone number"
                  value={transferToPhone}
                  onChange={(e) => {
                    setTransferToPhone(e.target.value);
                    setTransferError('');
                    setTransferSuccess('');
                  }}
                  onBlur={() => refreshTransferPreview('to', transferToPhone)}
                  disabled={transferLoading}
                />
                {transferToPreview && (
                  <p className={`transfer-preview ${transferToPreview.missing ? 'is-missing' : ''}`}>
                    {transferToPreview.missing
                      ? `No customer found for ${transferToPreview.phone}`
                      : `${transferToPreview.name} · ${transferToPreview.tier} · ${transferToPreview.points.toLocaleString()} pts`}
                  </p>
                )}
              </div>

              <div className="transfer-field">
                <label htmlFor="transfer-amount">Points to transfer</label>
                <input
                  id="transfer-amount"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="e.g. 500"
                  value={transferAmount}
                  onChange={(e) => {
                    setTransferAmount(e.target.value);
                    setTransferError('');
                    setTransferSuccess('');
                  }}
                  disabled={transferLoading}
                />
              </div>

              {transferError ? <p className="transfer-error">{transferError}</p> : null}
              {transferSuccess ? <p className="transfer-success">{transferSuccess}</p> : null}

              <div className="transfer-actions">
                <button
                  type="button"
                  className="modal-btn"
                  onClick={closeTransferModal}
                  disabled={transferLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-btn save-btn"
                  disabled={transferLoading}
                >
                  {transferLoading ? 'Transferring…' : 'Transfer points'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAuditModal && (
        <div className="modal-overlay" onClick={() => setShowAuditModal(false)}>
          <div className="modal-content rewards-audit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rewards audit log</h2>
              <button type="button" className="close-btn" onClick={() => setShowAuditModal(false)}>
                ×
              </button>
            </div>
            <div className="rewards-audit-toolbar">
              <label htmlFor="audit-event-filter">
                Event
                <select
                  id="audit-event-filter"
                  value={auditEventFilter}
                  onChange={(e) => setAuditEventFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="reward_redeemed">reward_redeemed</option>
                  <option value="reward_redeem_failed">reward_redeem_failed</option>
                  <option value="points_transferred">points_transferred</option>
                  <option value="tier_unlocked">tier_unlocked</option>
                  <option value="tier_tag_repaired">tier_tag_repaired</option>
                  <option value="otp_sent">otp_sent</option>
                  <option value="otp_verified">otp_verified</option>
                </select>
              </label>
              <button
                type="button"
                className="modal-btn save-btn"
                onClick={refreshAuditLog}
                disabled={auditLoading}
              >
                {auditLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {auditError ? <p className="rewards-audit-error">{auditError}</p> : null}
            {auditLoading && !auditEntries.length ? (
              <p className="rewards-audit-empty">Loading…</p>
            ) : auditEntries.length === 0 ? (
              <p className="rewards-audit-empty">No audit events yet.</p>
            ) : (
              <ul className="rewards-audit-list">
                {auditEntries.map((entry) => (
                  <li key={entry.id} className="rewards-audit-item">
                    <div className="rewards-audit-item-head">
                      <span className="rewards-audit-event">{entry.event}</span>
                      <span className="rewards-audit-time">
                        {entry.createdAt
                          ? new Date(entry.createdAt).toLocaleString()
                          : '—'}
                      </span>
                    </div>
                    <pre className="rewards-audit-details">
                      {JSON.stringify(entry.details, null, 2)}
                    </pre>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
